import { Button, Modal, Space, Table, Tag, Tooltip, Typography } from "antd"
import { ColumnsType } from "antd/lib/table"
import { PlusCircleOutlined } from "@ant-design/icons"
import { useState } from "react"
import { Form } from "antd"
import { buildNotificationFromPreset, getAvailableEventPresets, getGamePreset } from "domains/specialEvents/presets"
import { SpecialEventNotificationPreset, SpecialEventPresetsConfigType } from "domains/managingJson/store/types"

const { Text } = Typography

type Props = {
  parentIndex: number;
  presetsConfig: SpecialEventPresetsConfigType;
  add: (defaultValue?: unknown, insertIndex?: number) => void;
}

const renderDefaultTag = (value: number | undefined, suffix: string) => (
  value ? <Tag>{value}{suffix}</Tag> : <Tag>Not set</Tag>
)

export const EventPresetButton = ({ parentIndex, presetsConfig, add }: Props) => {
  const form = Form.useFormInstance()
  const [isOpen, setIsOpen] = useState(false)
  const gameId = Number(form.getFieldValue(["notificationsPerGame", parentIndex, "gameId"]))
  const gamePreset = getGamePreset(presetsConfig, gameId)
  // A game with no per-game preset is time-based: it still has the launch follow-up to offer,
  // and leaving the menu disabled is what pushes ops into hand-typing a trigger that never fires.
  const availablePresets = getAvailableEventPresets(presetsConfig, gameId)
    .filter(preset => preset.codeSupported !== false)

  const addPreset = (preset: SpecialEventNotificationPreset) => {
    add(buildNotificationFromPreset(preset, presetsConfig))
    setIsOpen(false)
  }

  const columns: ColumnsType<SpecialEventNotificationPreset> = [
    {
      title: "Friendly event",
      render: (_, preset) => (
        <Space direction="vertical" size={2}>
          <Tooltip title={preset.description}>
            <Text strong>{preset.friendlyName}</Text>
          </Tooltip>
          {preset.description && <Text type="secondary">{preset.description}</Text>}
          {preset.recommendationScore && <Text type="secondary">{preset.recommendationScore}</Text>}
        </Space>
      ),
    },
    {
      title: "Trigger key",
      width: 180,
      render: (_, preset) => <Text code>{preset.triggerEventName}</Text>,
    },
    {
      title: "Defaults",
      width: 220,
      render: (_, preset) => (
        <Space wrap>
          {renderDefaultTag(preset.defaults?.buffsReward ?? presetsConfig.defaultNotification?.buffsReward, " Buffs")}
          {renderDefaultTag(preset.defaults?.triggerDelay ?? presetsConfig.defaultNotification?.triggerDelay, "s delay")}
          {renderDefaultTag(preset.defaults?.duration ?? presetsConfig.defaultNotification?.duration, "s duration")}
        </Space>
      ),
    },
    {
      title: "Action",
      width: 100,
      render: (_, preset) => <Button size="small" type="primary" onClick={() => addPreset(preset)}>Add</Button>,
    },
  ]

  return (
    <>
      <Button
        type="primary"
        icon={<PlusCircleOutlined />}
        onClick={() => setIsOpen(true)}
        disabled={!availablePresets.length}
      >
        Add from preset
      </Button>
      <Modal
        width={920}
        visible={isOpen}
        title={`Add event preset - ${gamePreset?.gameName || "time-based game"}`}
        onCancel={() => setIsOpen(false)}
        footer={null}
      >
        <Table<SpecialEventNotificationPreset>
          rowKey="triggerEventName"
          size="small"
          pagination={false}
          dataSource={availablePresets}
          columns={columns}
          onRow={preset => ({ onDoubleClick: () => addPreset(preset) })}
        />
        <Text type="secondary">
          Unsupported spreadsheet-only events are hidden here. Use custom event when the app adds a new trigger before presets are updated.
        </Text>
      </Modal>
    </>
  )
}
